import { defineStore } from 'pinia'
import { isValidTimezone, type AppearanceSettings } from '@trading/core'
import {
  DEFAULT_TIMEZONE,
  CURRENCY_OPTIONS
} from '~/components/features/Settings/constants/settings-options'
import { useUserStore } from '~/stores/User'
import { services } from '~/services/composition'

const STORAGE_KEY = 'settings'

const SYNC_DEBOUNCE_MS = 400

type SettingsState = AppearanceSettings

const DEFAULT_CURRENCY = CURRENCY_OPTIONS[0]!.value

let syncTimer: ReturnType<typeof setTimeout> | null = null

// последний набор, подтверждённый бэком; к нему откатываемся, если сохранение упало
let lastSaved: SettingsState | null = null

function isValidCurrency(value: unknown): value is string {
  return typeof value === 'string' && CURRENCY_OPTIONS.some((option) => option.value === value)
}

function isSameSettings(a: SettingsState, b: SettingsState): boolean {
  return a.timezone === b.timezone && a.currency === b.currency && a.soundVolume === b.soundVolume
}

function notifySyncError() {
  const { $t } = useNuxtApp()
  useToast().add({ title: String($t('settings.syncError.title')), color: 'error' })
}

export function getDefaultTimezone(): string {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return isValidTimezone(browserTimezone) ? browserTimezone : DEFAULT_TIMEZONE
  } catch {
    return DEFAULT_TIMEZONE
  }
}

export const useSettingsStore = defineStore('settings', {
  state: (): SettingsState => ({
    timezone: DEFAULT_TIMEZONE,
    currency: DEFAULT_CURRENCY,
    soundVolume: 50
  }),

  actions: {
    setTimezone(timezone: string) {
      this.timezone = timezone
      this.persistToStorage()
      this.scheduleSync()
    },

    setCurrency(currency: string) {
      this.currency = currency
      this.persistToStorage()
      this.scheduleSync()
    },

    setSoundVolume(soundVolume: number) {
      this.soundVolume = soundVolume
      this.persistToStorage()
      this.scheduleSync()
    },

    hydrateFromLocalStorage() {
      if (typeof localStorage === 'undefined') return

      const raw = localStorage.getItem(STORAGE_KEY)
      let parsed: Partial<SettingsState> = {}

      if (raw) {
        try {
          const value: unknown = JSON.parse(raw)
          parsed = value && typeof value === 'object' ? (value as Partial<SettingsState>) : {}
        } catch (error) {
          console.warn('Failed to restore settings:', error)
          localStorage.removeItem(STORAGE_KEY)
        }
      }

      this.timezone = isValidTimezone(parsed.timezone) ? parsed.timezone : getDefaultTimezone()
      this.currency = isValidCurrency(parsed.currency) ? parsed.currency : this.currency
      this.soundVolume =
        typeof parsed.soundVolume === 'number' ? parsed.soundVolume : this.soundVolume

      // Create normalized settings for a new user or after localStorage was cleared.
      this.persistToStorage()
    },

    // Вызывается только для залогиненного пользователя (см. plugins/settings.client.ts)
    async hydrateFromBackend() {
      const local = this.getSettings()

      try {
        const settings = await services.settings.loadAppearance(local)

        // Пока шёл запрос, пользователь поменял настройку: его выбор новее ответа
        // и уже уходит на бэк через scheduleSync
        if (!isSameSettings(local, this.getSettings())) return

        if (settings) {
          this.$patch(settings)
          lastSaved = settings
          this.persistToStorage()
        } else {
          // Записи нет — выгружаем локальный набор целиком,
          // чтобы на других устройствах не было смеси с дефолтами
          await services.settings.saveAppearance(local)
          lastSaved = local
        }
      } catch (error) {
        console.warn('[settings] Failed to load "appearance" scope from backend', error)
      }
    },

    // Стор обновляется сразу, а на бэк уходит один запрос после паузы:
    // ползунок громкости вызывает сеттер на каждом шаге перетаскивания
    scheduleSync() {
      if (!useUserStore().isAuthenticated) return

      if (syncTimer) clearTimeout(syncTimer)
      syncTimer = setTimeout(() => {
        syncTimer = null
        // ждать незачем: ошибку сохранения syncToBackend обрабатывает сам
        void this.syncToBackend()
      }, SYNC_DEBOUNCE_MS)
    },

    cancelSync() {
      if (syncTimer) clearTimeout(syncTimer)
      syncTimer = null
      lastSaved = null
    },

    async syncToBackend() {
      const settings = this.getSettings()

      try {
        await services.settings.saveAppearance(settings)
        lastSaved = settings
      } catch (error) {
        console.warn('[settings] Failed to save "appearance" scope to backend', error)
        notifySyncError()

        // Пока запрос шёл, пользователь мог поменять что-то ещё — тогда уже запланирован
        // новый запрос с актуальным набором, и откат затёр бы свежий выбор
        if (lastSaved && !syncTimer) {
          this.$patch(lastSaved)
          this.persistToStorage()
        }
      }
    },

    getSettings(): SettingsState {
      return {
        timezone: this.timezone,
        currency: this.currency,
        soundVolume: this.soundVolume
      }
    },

    persistToStorage() {
      if (typeof localStorage === 'undefined') return

      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.getSettings()))
    }
  }
})
